/**
 * AoneGND storefront integration: customer picker + toast.
 * window.AonegndCustomerPicker.open(options) => Promise<{id,name,customerNumber}|null>
 * window.AonegndToast.show(message, type)
 */
(function (win) {
  'use strict';

  var FOCUSABLE =
    'button:not([disabled]), [href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /** @type {HTMLElement|null} */
  var toastHost = null;

  /** @param {string} base @param {string} path */
  function joinUrl(base, path) {
    var b = String(base || '').replace(/\/+$/, '');
    var p = String(path || '');
    if (!p) return b;
    if (/^https?:\/\//i.test(p)) return p;
    if (p.charAt(0) !== '/') p = '/' + p;
    return b ? b + p : p;
  }

  /** @param {unknown} payload */
  function unwrapApiData(payload) {
    if (payload && typeof payload === 'object') {
      var root = /** @type {Record<string, unknown>} */ (payload);
      if (root.success === true && root.data && typeof root.data === 'object') {
        return root.data;
      }
      if (root.data && typeof root.data === 'object') {
        return root.data;
      }
    }
    return payload;
  }

  /** @param {string} url */
  function getStorefrontApiHeaders(url) {
    /** @type {Record<string, string>} */
    var headers = { Accept: 'application/json' };
    try {
      var parsed = new URL(url, typeof window !== 'undefined' ? window.location.origin : 'https://localhost');
      var host = (parsed.hostname || '').toLowerCase();
      if (host.indexOf('ngrok') >= 0) headers['ngrok-skip-browser-warning'] = 'true';
    } catch (_) {}
    return headers;
  }

  /** @param {string} url @param {RequestInit} [opts] */
  async function fetchJson(url, opts) {
    var apiHeaders = getStorefrontApiHeaders(url);
    var res = await fetch(url, {
      mode: 'cors',
      credentials: 'omit',
      ...(opts || {}),
      headers: {
        ...apiHeaders,
        ...(opts && opts.headers ? opts.headers : {}),
      },
    });
    var txt = await res.text();
    /** @type {Record<string, unknown>} */
    var data = {};
    try {
      data = txt ? JSON.parse(txt) : {};
    } catch {
      data = {};
    }
    return { res, data };
  }

  /** @param {string} message @param {'error'|'success'|'info'} [type] */
  function showToast(message, type) {
    if (!toastHost) {
      toastHost = document.createElement('div');
      toastHost.className = 'aonegnd-toast-host';
      toastHost.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastHost);
    }
    var el = document.createElement('div');
    el.className = 'aonegnd-toast';
    if (type === 'error') el.classList.add('aonegnd-toast--error');
    if (type === 'success') el.classList.add('aonegnd-toast--success');
    el.textContent = message;
    toastHost.appendChild(el);
    requestAnimationFrame(function () {
      el.classList.add('is-visible');
    });
    window.setTimeout(function () {
      el.classList.remove('is-visible');
      window.setTimeout(function () {
        el.remove();
      }, 300);
    }, 4200);
  }

  /** @param {Element} container */
  function focusableList(container) {
    var nodes = container.querySelectorAll(FOCUSABLE);
    return Array.prototype.filter.call(nodes, function (el) {
      return (
        el instanceof HTMLElement &&
        !el.hasAttribute('hidden') &&
        (el.offsetParent !== null || (el.getClientRects && el.getClientRects().length > 0))
      );
    });
  }

  /** @type {{ modal: HTMLElement|null, resolve: ((value: {id:number|string,name:string,customerNumber:string}|null) => void)|null, customers: Array<{id:number|string,name:string,customerNumber:string}>, selectedId: string|null, escHandler: ((e: KeyboardEvent) => void)|null, tabHandler: ((e: KeyboardEvent) => void)|null, lastFocus: Element|null, htmlOverflow: string, bodyOverflow: string }} */
  var pickerState = {
    modal: null,
    resolve: null,
    customers: [],
    selectedId: null,
    escHandler: null,
    tabHandler: null,
    lastFocus: null,
    htmlOverflow: '',
    bodyOverflow: '',
  };

  /** @param {HTMLElement} modal */
  function getPickerParts(modal) {
    return {
      search: modal.querySelector('[data-aonegnd-customer-picker-search]'),
      list: modal.querySelector('[data-aonegnd-customer-picker-list]'),
      status: modal.querySelector('[data-aonegnd-customer-picker-status]'),
      done: modal.querySelector('[data-aonegnd-customer-picker-done]'),
      title: modal.querySelector('[data-aonegnd-customer-picker-title]'),
      hint: modal.querySelector('[data-aonegnd-customer-picker-hint]'),
      dialog: modal.querySelector('[role="dialog"]'),
    };
  }

  /** @param {HTMLElement} modal @param {{id:number|string,name:string,customerNumber:string}|null} value */
  function finishPicker(modal, value) {
    var parts = getPickerParts(modal);
    if (parts.dialog && pickerState.tabHandler) {
      parts.dialog.removeEventListener('keydown', pickerState.tabHandler);
    }
    pickerState.tabHandler = null;
    if (pickerState.escHandler) {
      document.removeEventListener('keydown', pickerState.escHandler, true);
    }
    pickerState.escHandler = null;

    document.documentElement.style.overflow = pickerState.htmlOverflow;
    document.body.style.overflow = pickerState.bodyOverflow;
    document.body.classList.remove('aonegnd-customer-picker-open');

    modal.setAttribute('hidden', '');
    modal.setAttribute('aria-hidden', 'true');

    var fn = pickerState.resolve;
    var refocus = pickerState.lastFocus;
    pickerState.modal = null;
    pickerState.resolve = null;
    pickerState.customers = [];
    pickerState.selectedId = null;
    pickerState.lastFocus = null;

    if (fn) fn(value);

    if (refocus && typeof refocus.focus === 'function') {
      try {
        refocus.focus();
      } catch (_) {}
    }
  }

  /** @param {HTMLElement} modal */
  function updateDoneButton(modal) {
    var parts = getPickerParts(modal);
    if (!(parts.done instanceof HTMLButtonElement)) return;
    parts.done.disabled = !pickerState.selectedId;
  }

  /** @param {Record<string, unknown>} raw */
  function resolveCustomerNumber(raw) {
    var candidates = [
      raw.customerNumber,
      raw.customer_number,
      raw.customerNo,
      raw.customer_no,
      raw.customerCode,
      raw.customer_code,
      raw.code,
      raw.cardCode,
      raw.card_code,
      raw.number,
    ];
    for (var i = 0; i < candidates.length; i++) {
      if (candidates[i] == null) continue;
      var value = String(candidates[i]).trim();
      if (value) return value;
    }
    return '';
  }

  /** @param {unknown} customer */
  function normalizeCustomer(customer) {
    if (!customer || typeof customer !== 'object') return null;
    var raw = /** @type {Record<string, unknown>} */ (customer);
    var idValue =
      raw.id != null
        ? raw.id
        : raw.customerId != null
          ? raw.customerId
          : raw.customer_id != null
            ? raw.customer_id
            : raw.uid != null
              ? raw.uid
              : null;
    if (idValue == null) return null;
    var parsedId = Number(idValue);
    if (!Number.isInteger(parsedId) || parsedId <= 0) return null;
    var nameValue =
      raw.name != null
        ? raw.name
        : raw.customerName != null
          ? raw.customerName
          : raw.customer_name != null
            ? raw.customer_name
            : raw.fullName != null
              ? raw.fullName
              : '';
    var customerNumber = resolveCustomerNumber(raw);
    var normalizedName = String(nameValue || '').trim();
    return {
      id: parsedId,
      name: normalizedName || customerNumber || String(parsedId),
      customerNumber: customerNumber,
    };
  }

  /** @param {HTMLElement} modal @param {string} query */
  function renderCustomerList(modal, query) {
    var parts = getPickerParts(modal);
    if (!(parts.list instanceof HTMLElement)) return;

    var q = String(query || '').trim().toLowerCase();
    var filtered = pickerState.customers.filter(function (c) {
      if (!q) return true;
      var name = String(c.name || '').toLowerCase();
      var number = String(c.customerNumber || '').toLowerCase();
      var id = String(c.id || '').toLowerCase();
      return name.indexOf(q) !== -1 || number.indexOf(q) !== -1 || id.indexOf(q) !== -1;
    });

    parts.list.innerHTML = '';
    if (!filtered.length) {
      var empty = document.createElement('li');
      empty.className = 'aonegnd-customer-picker__empty';
      empty.textContent = q ? 'No customers match your search.' : 'No customers found.';
      parts.list.appendChild(empty);
      return;
    }

    filtered.forEach(function (customer) {
      var id = String(customer.id);
      var li = document.createElement('li');
      var label = document.createElement('label');
      label.className = 'aonegnd-customer-picker__option';
      var radio = document.createElement('input');
      radio.type = 'radio';
      radio.name = 'sync-customer';
      radio.value = id;
      radio.checked = pickerState.selectedId === id;
      var text = document.createElement('span');
      var displayName = String(customer.name || '');
      var displayNumber = String(customer.customerNumber || '').trim();
      text.textContent = displayNumber ? displayNumber + ' - ' + displayName : displayName;
      label.appendChild(radio);
      label.appendChild(text);
      radio.addEventListener('change', function () {
        pickerState.selectedId = id;
        updateDoneButton(modal);
      });
      li.appendChild(label);
      parts.list.appendChild(li);
    });
  }

  /** @param {HTMLElement} modal @param {string} message */
  function setPickerStatus(modal, message) {
    var parts = getPickerParts(modal);
    if (!(parts.status instanceof HTMLElement)) return;
    if (!message) {
      parts.status.hidden = true;
      parts.status.textContent = '';
      return;
    }
    parts.status.hidden = false;
    parts.status.textContent = message;
  }

  /**
   * @param {{
   *   apiBaseUrl?: string,
   *   customersPath?: string,
   *   salesRepEmail?: string,
   *   title?: string,
   *   searchPlaceholder?: string,
   *   doneLabel?: string,
   *   cancelLabel?: string
   * }} options
   * @returns {Promise<{id:number|string,name:string,customerNumber:string}|null>}
   */
  function openCustomerPicker(options) {
    options = options || {};
    var modal = document.querySelector('[data-aonegnd-customer-picker]');
    if (!(modal instanceof HTMLElement)) {
      return Promise.resolve(null);
    }

    return new Promise(function (resolve) {
      var parts = getPickerParts(modal);
      if (parts.title && options.title) parts.title.textContent = String(options.title);
      if (parts.search instanceof HTMLInputElement) {
        parts.search.value = '';
        if (options.searchPlaceholder) parts.search.placeholder = String(options.searchPlaceholder);
      }
      if (parts.done instanceof HTMLButtonElement && options.doneLabel) {
        parts.done.textContent = String(options.doneLabel);
      }
      var cancelButtons = modal.querySelectorAll('[data-aonegnd-customer-picker-dismiss]');
      if (options.cancelLabel && cancelButtons.length) {
        cancelButtons.forEach(function (btn) {
          if (btn.classList.contains('aonegnd-customer-picker__cancel')) {
            btn.textContent = String(options.cancelLabel);
          }
        });
      }

      pickerState.modal = modal;
      pickerState.resolve = resolve;
      pickerState.customers = [];
      pickerState.selectedId = null;
      pickerState.lastFocus = document.activeElement;
      pickerState.htmlOverflow = document.documentElement.style.overflow;
      pickerState.bodyOverflow = document.body.style.overflow;

      modal.removeAttribute('hidden');
      modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('aonegnd-customer-picker-open');
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';

      updateDoneButton(modal);
      renderCustomerList(modal, '');
      setPickerStatus(modal, 'Loading customers...');

      pickerState.escHandler = function (e) {
        if (e.key === 'Escape') finishPicker(modal, null);
      };
      document.addEventListener('keydown', pickerState.escHandler, true);

      if (parts.dialog instanceof HTMLElement) {
        pickerState.tabHandler = function (e) {
          if (e.key !== 'Tab') return;
          var list = focusableList(parts.dialog);
          if (!list.length) return;
          var first = list[0];
          var last = list[list.length - 1];
          if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        };
        parts.dialog.addEventListener('keydown', pickerState.tabHandler);
      }

      modal.querySelectorAll('[data-aonegnd-customer-picker-dismiss]').forEach(function (el) {
        el.addEventListener(
          'click',
          function () {
            finishPicker(modal, null);
          },
          { once: true }
        );
      });

      if (parts.done instanceof HTMLButtonElement) {
        parts.done.addEventListener(
          'click',
          function () {
            if (!pickerState.selectedId) return;
            var match = pickerState.customers.find(function (c) {
              return String(c.id) === pickerState.selectedId;
            });
            finishPicker(modal, match || null);
          },
          { once: true }
        );
      }

      if (parts.search instanceof HTMLInputElement) {
        parts.search.addEventListener('input', function () {
          renderCustomerList(modal, parts.search instanceof HTMLInputElement ? parts.search.value : '');
        });
      }

      var base = String(options.apiBaseUrl || '').replace(/\/+$/, '');
      var customersPath = String(options.customersPath || '/public/sales-rep/customers').replace(/\/+$/, '');
      var email = encodeURIComponent(String(options.salesRepEmail || '').trim());
      var url = joinUrl(base, customersPath) + '?salesRepEmail=' + email;

      fetchJson(url)
        .then(function (result) {
          if (!result.res.ok) {
            var msg =
              typeof result.data.message === 'string'
                ? result.data.message
                : 'Unable to load customers.';
            setPickerStatus(modal, msg);
            showToast(msg, 'error');
            return;
          }
          var data = unwrapApiData(result.data);
          var customersRaw =
            data && typeof data === 'object' && Array.isArray(/** @type {Record<string,unknown>} */ (data).customers)
              ? /** @type {Array<{id:number|string,name:string}>} */ (
                  /** @type {Record<string,unknown>} */ (data).customers
                )
              : [];
          pickerState.customers = customersRaw
            .map(function (entry) {
              return normalizeCustomer(entry);
            })
            .filter(function (entry) {
              return !!entry;
            });
          setPickerStatus(modal, '');
          renderCustomerList(modal, '');
          if (parts.search instanceof HTMLInputElement) parts.search.focus();
        })
        .catch(function () {
          setPickerStatus(modal, 'Network error while loading customers.');
          showToast('Network error while loading customers.', 'error');
        });
    });
  }

  win.AonegndToast = { show: showToast };
  win.AonegndCustomerPicker = { open: openCustomerPicker };
})(typeof window !== 'undefined' ? window : globalThis);
